import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { EntityForm } from '@/components/forms/EntityForm';
import { EntityFormHeaderActions } from '@/components/forms/EntityFormHeaderActions';
import {
  useAccount,
  useUpdateAccount,
} from '@/features/accounts/accounts.hooks';
import { accountDetailLayout } from '@/features/accounts/accounts.schemas';
import { PermissionsGrid } from './components/PermissionsGrid';
import { SecuritySection } from './components/SecuritySection';
import { TagAuthSection } from './components/TagAuthSection';
import { ClientsTable } from './components/ClientsTable';
import { LoginNameTag } from './components/LoginNameTag';
import { useEntityForm } from '@/hooks/useEntityForm';
import { getDirtyValues } from '@/lib/form-utils';
import { toast } from 'sonner';
import { useState, useMemo } from 'react';
import { AdminAccountPatch } from '@certhon/models';
import { Badge } from '@/components/ui/Badge/badge';
import { getAccountActions, adaptActionsForPanel } from './accounts.actions';

export const AccountDetailsPanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const { accountId } = params;
  const accountQuery = useAccount(parseInt(accountId));
  const updateAccountMutation = useUpdateAccount();
  const [permissions, setPermissions] = useState<number | null>(null);

  const entityForm = useEntityForm({
    layout: accountDetailLayout,
    ...accountQuery,
  });

  // Initialize permissions from account data
  const currentPermissions = permissions ?? accountQuery.data?.permissions ?? 0;

  const account = accountQuery.data;
  const title = account
    ? `${account.name || account.loginname || account.email}`
    : `Account ${accountId}`;

  const headerActions = (
    <EntityFormHeaderActions
      entityForm={entityForm}
      formId="account-details-form"
      // Permissions live outside the form
      isDirty={
        permissions !== null && permissions !== accountQuery.data?.permissions
      }
      onCancel={() => setPermissions(null)}
    />
  );

  // Get actions for the dropdown menu
  const actions = useMemo(() => {
    if (!account) return [];
    const rowActions = getAccountActions(account);
    return adaptActionsForPanel(rowActions, account);
  }, [account]);

  // Tag auth handlers
  const handleApproveRequest = async () => {
    if (!account) return;
    await updateAccountMutation.mutateAsync({
      recnum: account.recnum,
      enable_requested_tag_id: true,
    });
  };

  const handleDenyRequest = async () => {
    if (!account) return;
    await updateAccountMutation.mutateAsync({
      recnum: account.recnum,
      enable_requested_tag_id: false,
    });
  };

  const handlePreApprove = async () => {
    if (!account) return;
    await updateAccountMutation.mutateAsync({
      recnum: account.recnum,
      tag_authentication_preapproved: true,
    });
  };

  const handleRemovePreApproval = async () => {
    if (!account) return;
    await updateAccountMutation.mutateAsync({
      recnum: account.recnum,
      tag_authentication_preapproved: false,
    });
  };

  const handleDisableTag = async () => {
    if (!account) return;
    await updateAccountMutation.mutateAsync({
      recnum: account.recnum,
      tag_id: null,
    });
  };

  return (
    <Panel
      {...panelProps}
      title={title}
      actions={entityForm.formMode === 'view' ? actions : undefined}
      headerActions={headerActions}
    >
      <div className="space-y-6">
        {/* Header with status badges */}
        {account && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <LoginNameTag loginname={account.loginname} />
            <span>•</span>
            <span>{account.email}</span>
            <span className="ml-auto flex gap-2">
              <Badge variant={account.activated ? 'default' : 'secondary'}>
                {account.activated ? 'Active' : 'Inactive'}
              </Badge>
              {account.internal && <Badge variant="outline">Internal</Badge>}
            </span>
          </div>
        )}

        {/* Main form */}
        <EntityForm
          {...entityForm}
          id="account-details-form"
          hideActions={true}
          onSubmit={entityForm.form.handleSubmit(
            async () => {
              const dirtyValues = getDirtyValues(
                entityForm.form.formState.dirtyFields,
                entityForm.form.getValues(),
              );

              // Include permissions if changed
              const patch: Partial<AdminAccountPatch> & { recnum: number } = {
                recnum: parseInt(accountId),
                ...dirtyValues,
              };

              if (
                permissions !== null &&
                permissions !== accountQuery.data?.permissions
              ) {
                patch.permissions = permissions;
              }

              await updateAccountMutation.mutateAsync(patch);
              entityForm.setFormMode('view');
              setPermissions(null);
            },
            (errors) => {
              console.log('Form validation errors:', { errors });
              toast.error('Please fix the validation errors');
            },
          )}
        />

        {/* Permissions section */}
        {account && (
          <div className="space-y-2">
            <h3 className="font-medium">Permissions</h3>
            <PermissionsGrid
              account={account}
              value={currentPermissions}
              onChange={setPermissions}
              disabled={entityForm.formMode === 'view'}
            />
          </div>
        )}

        {/* Collapsible sections */}
        {account && (
          <div className="space-y-3">
            <SecuritySection account={account} />
            <TagAuthSection
              account={account}
              onApproveRequest={handleApproveRequest}
              onDenyRequest={handleDenyRequest}
              onPreApprove={handlePreApprove}
              onRemovePreApproval={handleRemovePreApproval}
              onDisable={handleDisableTag}
              disabled={updateAccountMutation.isPending}
            />
            <ClientsTable clients={account.clientInfo} />
          </div>
        )}
      </div>
    </Panel>
  );
};
